import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Pressable } from 'react-native';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { useIsMobile } from '../../hooks/useIsMobile';

const WebModal = ({
  visible,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 600,
  showBackButton = false,
  onBack,
  backAccessibilityLabel = 'Back',
  footer,
  headerStyle,
  overlay,
}) => {
  const isMobile = useIsMobile();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        style={[styles.overlay, isMobile && styles.overlayMobile]}
        onPress={(event) => {
          // Nested dialogs use portals on web; only a direct backdrop press closes this one.
          if (event.target === event.currentTarget) onClose?.();
        }}
      >
        <Pressable style={[styles.modal, { maxWidth }]} onPress={e => e.stopPropagation()}>
          {/* Header */}
          <View style={[styles.header, isMobile && styles.headerMobile, headerStyle]}>
            {showBackButton && (
              <TouchableOpacity
                style={[styles.backBtn, isMobile && styles.backBtnMobile]}
                onPress={onBack}
                accessibilityRole="button"
                accessibilityLabel={backAccessibilityLabel}
              >
                <Text style={styles.backBtnText}>←</Text>
              </TouchableOpacity>
            )}
            <View style={showBackButton ? styles.headerTextWithBack : styles.headerText}>
              <Text style={[styles.title, isMobile && styles.titleMobile]}>{title}</Text>
              {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
            </View>
          </View>

          {/* Body */}
          <View style={[styles.body, isMobile && styles.bodyMobile]}>
            {children}
          </View>

          {/* Footer */}
          {footer && (
            <View style={styles.footer}>
              {footer}
            </View>
          )}
        </Pressable>
      </Pressable>
      {overlay}
    </Modal>
  );
};

// Pre-built footer buttons
export const WebModalFooter = ({
  onBack,
  onNext,
  backLabel = 'Back',
  nextLabel = 'Next',
  nextDisabled = false,
}) => (
  <View style={styles.footerButtons}>
    {onBack && (
      <TouchableOpacity style={styles.btnBack} onPress={onBack}>
        <Text style={styles.btnBackText}>{backLabel}</Text>
      </TouchableOpacity>
    )}
    <TouchableOpacity
      style={[styles.btnNext, nextDisabled && styles.btnNextDisabled]}
      onPress={onNext}
      disabled={nextDisabled}
    >
      <Text style={[styles.btnNextText, nextDisabled && styles.btnNextTextDisabled]}>
        {nextLabel}
      </Text>
    </TouchableOpacity>
  </View>
);

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    ...webShadows.modal,
  },
  header: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    position: 'relative',
  },
  headerText: {
    // Default header text alignment
  },
  headerTextWithBack: {
    paddingLeft: 48,
  },
  backBtn: {
    position: 'absolute',
    top: 24,
    left: 24,
    width: 36,
    height: 36,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    cursor: 'pointer',
  },
  backBtnText: {
    fontSize: 18,
    color: webColors.textPrimary,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: webColors.primary,
  },
  body: {
    padding: 24,
    maxHeight: '60vh',
    overflow: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  footer: {
    padding: 16,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  btnBack: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  btnNext: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnNextDisabled: {
    backgroundColor: webColors.primaryLight,
  },
  btnNextText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnNextTextDisabled: {
    color: '#f9a8d4',
  },
  // ==================== MOBILE VARIANTS ====================
  overlayMobile: {
    padding: 12,
  },
  headerMobile: {
    padding: 16,
  },
  backBtnMobile: {
    top: 16,
    left: 16,
  },
  titleMobile: {
    fontSize: 18,
  },
  bodyMobile: {
    padding: 16,
  },
});

export default WebModal;
